import { detailOr } from "@/lib/apiErrorMessage";
import i18n from "@/i18n";
/**
 * ProductUnitReview — Batch C (2026-02).
 *
 * Chef-facing screen to review canonical unit assignments for products
 * whose physical unit could not be determined confidently from
 * invoices. Each candidate shows the evidence and a proposed pack
 * conversion; the chef must explicitly confirm each row.
 *
 * See RCA 2026-02 §5 / §15. Frontend counterpart of
 * GET /api/products/unit-review/candidates and
 * POST /api/products/{id}/confirm-unit.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function ProductUnitReview() {
    const { user } = useApp();
    const userRestaurantId = user?.restaurant_id;
    const params = useMemo(() => (userRestaurantId
        ? { restaurant_id: userRestaurantId } : {}), [userRestaurantId]);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [physicalUnit, setPhysicalUnit] = useState("");
    const [packSize, setPackSize] = useState("");
    const [packUnit, setPackUnit] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const { data } = await api.get(
                "/products/unit-review/candidates", { params });
            setCandidates(data.candidates || []);
        } catch (e) {
            setError(detailOr(e?.response?.data?.detail, i18n.t("ui.productUnitReview.unableToLoadReviewCandidatesTry", "Unable to load review candidates. Try again in a moment.")));
        } finally {
            setLoading(false);
        }
    }, [params]);
    useEffect(() => { refresh(); }, [refresh]);

    const openConfirm = (c) => {
        setSelected(c);
        setPhysicalUnit(c.suggested_physical_unit || c.current_unit || "");
        setPackSize(c.suggested_pack_size ?? "");
        setPackUnit(c.suggested_pack_unit || "");
    };

    const submitConfirm = async () => {
        if (!selected) return;
        if (!physicalUnit) {
            toast.error(i18n.t("ui.productUnitReview.pleaseSelectAPhysicalUnit", "Please select a physical unit")); return;
        }
        setBusy(true);
        try {
            await api.post(`/products/${selected.product_id}/confirm-unit`, {
                physical_unit: physicalUnit,
                pack_size: packSize ? Number(packSize) : null,
                pack_unit: packUnit || null,
                purchase_unit: selected.current_unit,
            }, { params });
            toast.success(`Unit confirmed: ${physicalUnit}`);
            setSelected(null);
            await refresh();
        } catch (e) {
            toast.error(detailOr(e?.response?.data?.detail, i18n.t("ui.productUnitReview.unableToSavePleaseTryAgain", "Unable to save. Please try again.")));
        } finally {
            setBusy(false);
        }
    };

    const previewPrice = (() => {
        if (!selected || !packSize) return null;
        const p = Number(selected.current_last_price || 0);
        const s = Number(packSize || 0);
        if (!s || !p) return null;
        return (p / s).toFixed(3);
    })();

    return (
        <div className="p-6 space-y-6" data-testid="unit-review-page">
            <div>
                <h1 className="text-2xl font-display font-bold">{i18n.t("ui.productUnitReview.productUnitReview", "Product Unit Review")}</h1>
                <p className="text-sm text-neutral-600 mt-1">
                    {i18n.t("ui.productUnitReview.confirmThePhysicalInventoryUnitFor", "Confirm the physical inventory unit for products whose canonical unit was not established by invoice extraction. This drives Inventory, Waste, Recipe costing and Purchase Needs.")}
                </p>
            </div>

            {loading && (
                <div className="text-sm text-neutral-500" data-testid="review-loading">
                    {i18n.t("ui.productUnitReview.loadingCandidates", "Loading candidates…")}
                </div>
            )}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800"
                     data-testid="review-error">{error}</div>
            )}
            {!loading && !error && candidates.length === 0 && (
                <div className="p-6 bg-neutral-50 border border-neutral-200 rounded-md text-sm"
                     data-testid="review-empty">
                    {i18n.t("ui.productUnitReview.noProductsNeedUnitReviewNew", "No products need unit review. New products captured from confirmed supplier deliveries will appear here if their physical unit is uncertain.")}
                </div>
            )}

            {!loading && !error && candidates.length > 0 && (
                <div className="border border-neutral-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="review-table">
                        <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
                            <tr>
                                <th className="text-left px-4 py-3">{i18n.t("ui.productUnitReview.product", "Product")}</th>
                                <th className="text-left px-4 py-3">{i18n.t("ui.productUnitReview.currentUnit", "Current unit")}</th>
                                <th className="text-left px-4 py-3">{i18n.t("ui.productUnitReview.suggested", "Suggested")}</th>
                                <th className="text-left px-4 py-3">{i18n.t("ui.productUnitReview.evidence", "Evidence")}</th>
                                <th className="text-left px-4 py-3">{i18n.t("ui.productUnitReview.confidence", "Confidence")}</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidates.map((c) => (
                                <tr key={c.product_id} className="border-t border-neutral-100"
                                    data-testid={`review-row-${c.product_id}`}>
                                    <td className="px-4 py-3 font-medium">
                                        {c.product_name}
                                        {c.barcode && (
                                            <div className="text-xs text-neutral-500">
                                                Barcode: {c.barcode}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div>{c.current_unit || "—"}</div>
                                        {c.current_last_price && (
                                            <div className="text-xs text-neutral-500">
                                                €{Number(c.current_last_price).toFixed(3)} /{" "}
                                                {c.current_last_price_unit || c.current_unit}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {c.suggested_pack_size && c.suggested_pack_unit ? (
                                            <>
                                                <div className="font-medium">
                                                    1 {c.current_unit} = {c.suggested_pack_size} {c.suggested_pack_unit}
                                                </div>
                                                <div className="text-xs text-neutral-500">
                                                    → Physical: {c.suggested_physical_unit}
                                                </div>
                                            </>
                                        ) : (
                                            <span className="text-xs text-neutral-500">
                                                {i18n.t("ui.productUnitReview.manualReviewRequired", "Manual review required")}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {c.supporting_deliveries} delivery{c.supporting_deliveries !== 1 ? "ies" : "y"}
                                        {c.distinct_received_units?.length > 0 && (
                                            <div className="text-neutral-500">
                                                Units seen: {c.distinct_received_units.join(", ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={
                                            c.confidence === "high" ? "text-emerald-700" :
                                            c.confidence === "conflict" ? "text-amber-700" :
                                            c.confidence === "low" ? "text-neutral-500" :
                                            "text-neutral-700"
                                        }>
                                            {c.confidence}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Button size="sm" variant="outline"
                                            data-testid={`review-open-${c.product_id}`}
                                            onClick={() => openConfirm(c)}>
                                            {i18n.t("ui.productUnitReview.review", "Review")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Confirmation dialog with unit-change preview. */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-lg" data-testid="review-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("ui.productUnitReview.confirmCanonicalUnit", "Confirm canonical unit")}</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4 text-sm">
                            <div className="font-medium">{selected.product_name}</div>
                            <div className="grid grid-cols-2 gap-3 p-3 bg-neutral-50 rounded-md">
                                <div>
                                    <div className="text-xs text-neutral-500">{i18n.t("ui.productUnitReview.old", "Old")}</div>
                                    <div>{selected.current_unit || "—"}</div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        €{Number(selected.current_last_price || 0).toFixed(3)} /{" "}
                                        {selected.current_last_price_unit || selected.current_unit}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-neutral-500">{i18n.t("ui.productUnitReview.new", "New")}</div>
                                    <div className="font-medium">{physicalUnit || "—"}</div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        {previewPrice
                                            ? <>€{previewPrice} / {physicalUnit}</>
                                            : "—"}
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="text-xs uppercase text-neutral-500">{i18n.t("ui.productUnitReview.physicalUnit", "Physical unit")}</label>
                                    <input data-testid="physical-unit-input"
                                        className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded"
                                        value={physicalUnit}
                                        onChange={(e) => setPhysicalUnit(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase text-neutral-500">{i18n.t("ui.productUnitReview.packSize", "Pack size")}</label>
                                    <input data-testid="pack-size-input"
                                        type="number" step="0.001"
                                        className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded"
                                        value={packSize}
                                        onChange={(e) => setPackSize(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase text-neutral-500">{i18n.t("ui.productUnitReview.packUnit", "Pack unit")}</label>
                                    <input data-testid="pack-unit-input"
                                        className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded"
                                        value={packUnit}
                                        onChange={(e) => setPackUnit(e.target.value)}
                                    />
                                </div>
                            </div>
                            {selected.distinct_received_units?.length > 1 && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                                    <b>{i18n.t("ui.productUnitReview.warning", "Warning")}</b>: {i18n.t("ui.productUnitReview.mixedUnits", "this product's stock history contains mixed units ({{units}}).", { units: selected.distinct_received_units.join(", ") })}{" "}
                                    {i18n.t("ui.productUnitReview.afterConfirmingACanonicalUnitA", "After confirming a canonical unit, a new physical Inventory Count is required to establish a trustworthy baseline.")}
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="outline" onClick={() => setSelected(null)}
                                    data-testid="review-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                                <Button onClick={submitConfirm} disabled={busy}
                                    data-testid="review-submit">
                                    {busy ? i18n.t("common.saving", "Saving…") : i18n.t("ui.productUnitReview.confirmChange", "Confirm change")}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
